import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  CircularProgress,
  List,
  Paper,
  Typography,
  FormControl,
  Select,
  MenuItem,
  Divider,
} from "@mui/material";
import { useTenant } from "../../contexts/tenant-context";
import {
  AuditLogDto,
  useAuditLogService,
} from "../../services/auditLogService";
import { useTenantManagementService } from "../../services/tenantManagementService";

const GLOBAL_ADMIN_TENANT_ID = "__GLOBAL_ADMIN__";
const RECENT_ACTIVITY_PAGE_SIZE = 10;

/** Same as Audit Log page — hide document-search audit rows from the widget. */
const isDocumentSearchAudit = (item: AuditLogDto) => item.entityId === "search";

type ActivityTenantOption = {
  tenantId: string;
  tenantName: string;
};

const panelSx = {
  bgcolor: "#fff",
  border: "1px solid #fff",
  borderRadius: 2,
  color: "#fff",
  overflow: "hidden",
};

interface RecentActivityPanelProps {
  title?: string;
  minHeight?: number;
}

const RecentActivityPanel: React.FC<RecentActivityPanelProps> = ({
  title = "Activity Monitor",
  minHeight = 460,
}) => {
  const {
    selectedTenantId,
    availableTenants,
    isGlobalAdmin,
  } = useTenant();

  const isActingAsGlobalAdmin =
    isGlobalAdmin &&
    (!selectedTenantId || selectedTenantId === GLOBAL_ADMIN_TENANT_ID);

  const fallbackTenantOptions = React.useMemo<ActivityTenantOption[]>(
    () =>
      (availableTenants ?? [])
        .filter((t) => t.tenantId !== GLOBAL_ADMIN_TENANT_ID)
        .map((t) => ({ tenantId: t.tenantId, tenantName: t.tenantName })),
    [availableTenants]
  );

  const { getAuditLogs } = useAuditLogService();
  const { getTenants } = useTenantManagementService();

  const [items, setItems] = useState<AuditLogDto[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [tenantOptions, setTenantOptions] = useState<ActivityTenantOption[]>(
    fallbackTenantOptions
  );
  const [selectedLogTenantId, setSelectedLogTenantId] = useState<string>("");

  const canLoadActivity = Boolean(selectedTenantId) || isActingAsGlobalAdmin;
  const effectiveTenantId = isActingAsGlobalAdmin
    ? (selectedLogTenantId || undefined)
    : (selectedTenantId || undefined);

  const shouldLoadActivity = isActingAsGlobalAdmin
    ? Boolean(selectedLogTenantId)
    : canLoadActivity;

  useEffect(() => {
    if (!isActingAsGlobalAdmin) {
      setTenantOptions(fallbackTenantOptions);
      setSelectedLogTenantId("");
      return;
    }

    let isMounted = true;

    const loadTenantOptions = async () => {
      try {
        const tenantDtos = await getTenants();
        if (!isMounted) return;

        if (!tenantDtos.length) {
          setTenantOptions(fallbackTenantOptions);
          return;
        }

        const mappedOptions = tenantDtos
          .filter((t) => typeof t.id === "string" && t.id.trim().length > 0)
          .map((t) => ({
            tenantId: t.id,
            tenantName: t.name,
          }));

        // De-duplicate by tenant id to avoid repeated options.
        const uniqueById = Array.from(
          new Map(mappedOptions.map((t) => [t.tenantId, t])).values()
        );

        setTenantOptions(uniqueById);
        setSelectedLogTenantId((prev) => {
          const stillExists = uniqueById.some((t) => t.tenantId === prev);
          return stillExists ? prev : (uniqueById[0]?.tenantId ?? "");
        });
      } catch {
        if (!isMounted) return;
        setTenantOptions(fallbackTenantOptions);
        setSelectedLogTenantId((prev) => {
          const stillExists = fallbackTenantOptions.some((t) => t.tenantId === prev);
          return stillExists ? prev : (fallbackTenantOptions[0]?.tenantId ?? "");
        });
      }
    };

    void loadTenantOptions();

    return () => {
      isMounted = false;
    };
  }, [isActingAsGlobalAdmin, getTenants, fallbackTenantOptions]);

  useEffect(() => {
    if (!shouldLoadActivity) {
      setItems([]);
      setErrorMessage(null);
      setIsLoading(false);
      return;
    }

    let isMounted = true;

    const loadRecentActivity = async () => {
      try {
        setIsLoading(true);
        setErrorMessage(null);

        // Same API as Audit Log page — widget just asks for the latest 10 rows.
        const paged = await getAuditLogs({
          page: 1,
          pageSize: RECENT_ACTIVITY_PAGE_SIZE,
          tenantId: effectiveTenantId,
          isGlobalAdminContext: isActingAsGlobalAdmin,
        });

        if (!isMounted) return;

        const result = (paged.items ?? []).filter((item) => !isDocumentSearchAudit(item));

        const getTenantIdFromLog = (item: AuditLogDto): string => {
          const raw = item as AuditLogDto & {
            tenantId?: string | null;
            tenantID?: string | null;
            tenant?: { id?: string | null } | null;
          };

          if (typeof raw.tenantId === "string") return raw.tenantId.trim();
          if (typeof raw.tenantID === "string") return raw.tenantID.trim();
          if (raw.tenant && typeof raw.tenant.id === "string") {
            return raw.tenant.id.trim();
          }

          return "";
        };

        const tenantIds = result.map(getTenantIdFromLog);
        const hasAnyTenantId = tenantIds.some((id) => id.length > 0);

        // In global-admin mode with tenant dropdown, ensure only selected-tenants are shown when tenant metadata exist
        if (isActingAsGlobalAdmin && hasAnyTenantId && selectedLogTenantId) {
          const tenantOnly = result.filter((_, index) => tenantIds[index] === selectedLogTenantId);
          setItems(tenantOnly.length > 0 ? tenantOnly : result);
          return;
        }

        setItems(result);
      } catch (error) {
        if (!isMounted) return;

        setItems([]);
        setErrorMessage(
          error instanceof Error
            ? error.message
            : "Unable to load recent activity."
        );
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    void loadRecentActivity();

    return () => {
      isMounted = false;
    };
  }, [
    shouldLoadActivity,
    effectiveTenantId,
    getAuditLogs,
    isActingAsGlobalAdmin,
    selectedLogTenantId,
  ]);

  if (!shouldLoadActivity) {
    return null;
  }

  return (
    <Paper sx={{ ...panelSx, minHeight }}>
      {isActingAsGlobalAdmin && tenantOptions.length > 0 && (
        <Box
        sx={{
          px: 2.5,
          py: 2,
          borderBottom: "2px solid #ff5b12",
          bgcolor: "rgba(255, 91, 18, 0.04)",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <Select
            value={selectedLogTenantId}
            onChange={(e) => setSelectedLogTenantId(e.target.value as string)}
            sx={{
              border: "1px solid #ff5b12",
              "& .MuiSelect-select": {
                color: "#ff5b12",
                WebkitTextFillColor: "#ff5b12",
              },
              "& .MuiOutlinedInput-notchedOutline": {
                borderColor: "#ff5b12",
              },
              "&:hover .MuiOutlinedInput-notchedOutline": {
                borderColor: "#ff7a33",
              },
              "& .MuiSvgIcon-root": {
                color: "#ff7a33",
              },
            }}
          >
            {tenantOptions.map((tenant) => (
              <MenuItem
                key={tenant.tenantId}
                value={tenant.tenantId}
                sx={{ color: "#ff5b12" }}
              >
                {tenant.tenantName}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>
      )}

      {isLoading && (
        <Box
          sx={{
            minHeight: "100% - 80px",
            maxHeight: "100vh - 80px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            overflow: "scroll-y",
          }}
        >
          <CircularProgress sx={{ color: "hsl(19 101% 51% / 1)", margin:"50%" }} />
        </Box>
      )}

      {!isLoading && errorMessage && (
        <Box sx={{ p: 2 }}>
          <Alert
            severity="error"
            sx={{
              bgcolor: "#2a0d00",
              color: "#fff",
              border: "1px solid #ff",
            }}
          >
            {errorMessage}
          </Alert>
        </Box>
      )}

      {!isLoading && !errorMessage && items.length === 0 && (
        <Box sx={{ p: 3 }}>
          <Typography
            variant="body2"
            sx={{ color: "#d0d0d0" }}
          >
            No recent activity found for the selected tenant.
          </Typography>
        </Box>
      )}

      {!isLoading && !errorMessage && items.length > 0 && (
       <Box sx={{
            maxHeight: "600px",
            overflowY: "scroll",
            overflowX: "hidden",
          }}> <List disablePadding>
          {items.map((item, index) => (
            <Box
              key={item.id}
              sx={{
                alignItems: "flex-start",
                px: 2.5,
                py: 2,
                backgroundColor: "hsl(19 101% 51% / .2)",
                paddingTop: "10px",
                margin: "5px",
                marginTop: "10px",
                marginRight: "10px",
                borderRadius: 2,
                border: "2px solid hsl(19 101% 51% / 1)",
              }}
            >
              <Typography fontWeight={700} color="#000" paddingBottom="10px">
                {item.details}
              </Typography>
              <Divider />
              <Typography paddingTop="10px" fontWeight={700} color="hsl(19 101% 51% / 1)">
                { new Date(item.timestamp).toLocaleString() }
                </Typography>
            </Box>  
          ))}
        </List>
        </Box>
      )}
      
    </Paper>
  );
};

export default RecentActivityPanel;